import { Minus, Plus } from "lucide-react";
import React from "react";
import Flex from "../../Flex";
import TextInput, { IProps as ITextInputProps } from "../TextInput";
import * as S from "./styled";

export interface IProps
    extends Omit<React.ComponentProps<typeof TextInput>, "value" | "defaultValue" | "onChange" | "type"> {
    value?: number | null;
    defaultValue?: number | null;
    onChange?: (value: number | null) => void;
    hideStepButtons?: boolean;
}

const stringToNumber = (str: string | undefined): number | null => {
    if (!str) {
        return null;
    }

    const value = parseInt(str, 10);

    if (Number.isNaN(value)) return null;

    return value;
};

const numberToString = (num: number | undefined | null) => {
    if (num === undefined) return undefined;
    if (num === null) return "";

    return num.toFixed();
};

const KEYS_TO_IGNORE = ["Comma", "Period", "Minus", "Plus", "KeyE"];
const DIGITS_REGEXP = /^-?\d*$/;

const NumberInput = React.forwardRef<HTMLInputElement, IProps>(
    (
        {
            value: propValue,
            defaultValue = null,
            disabled,
            onChange,
            step = 1,
            max,
            min,
            hideStepButtons = false,
            ...props
        },
        ref
    ) => {
        const [stateValue, setValue] = React.useState(defaultValue);

        const value = propValue === undefined ? stateValue : propValue;
        const stringValue = numberToString(value);

        const applyChange = (nextValue: number | null) => {
            setValue(nextValue);
            onChange?.(nextValue);
        };

        const handleChange: ITextInputProps["onChange"] = e => applyChange(stringToNumber(e.target.value));

        const handleIncrement = () => applyChange(value !== null ? value + 1 : 1);
        const handleDecrement = () => applyChange(value !== null ? value - 1 : 0);

        return (
            <S.NumberInput
                type="number"
                ref={ref}
                disabled={disabled}
                step={step}
                max={max}
                min={min}
                pattern={DIGITS_REGEXP}
                value={stringValue}
                onChange={handleChange}
                onKeyDown={event => {
                    if (KEYS_TO_IGNORE.includes(event.code)) {
                        event.preventDefault();
                    }
                }}
                trail={
                    !hideStepButtons && (
                        <Flex spacing={1}>
                            <S.RoundButton
                                type="button"
                                disabled={disabled || value === null || (!!min && value <= +min)}
                                icon={<Minus />}
                                onClick={handleDecrement}
                                trackName="decrement"
                            />
                            <S.RoundButton
                                type="button"
                                disabled={disabled || (!!max && value !== null && value >= +max)}
                                icon={<Plus />}
                                onClick={handleIncrement}
                                trackName="increment"
                            />
                        </Flex>
                    )
                }
                {...props}
            />
        );
    }
);

export default NumberInput;
